import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field } from "@/components/common";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { esCp, esCpIntl } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const O = String.fromCharCode(0xf3);
const I = String.fromCharCode(0xed);
const A = String.fromCharCode(0xe1);
const N = String.fromCharCode(0xf1);
const E = String.fromCharCode(0xe9);
const U = String.fromCharCode(0xfa);
const DASH = "\u2014";

export function fmtDomicilio(d) {
  const calle = String(d?.domicilio || "").trim();
  const cp = String(d?.cp || "").trim();
  const muni = String(d?.municipio || "").trim();
  const prov = String(d?.provincia || "").trim();
  const parts = [];
  if (calle) parts.push(calle);
  const bloque = [cp, muni].filter(Boolean).join(" ");
  if (bloque && !calle.toLowerCase().includes(bloque.toLowerCase())) parts.push(bloque);
  const joined = parts.join(", ");
  if (prov && !joined.toLowerCase().includes(prov.toLowerCase())) parts.push(prov);
  return parts.join(", ");
}

const EMPTY_DOM = { alias: "", domicilio: "", municipio: "", cp: "", provincia: "" };

export async function crearDomicilioCliente(nif, form, tipo = "cliente") {
  const { data } = await api.post("/api/v1/domicilios", {
    cliente_nif: String(nif || "").trim().toUpperCase(),
    tipo: tipo === "cargador" ? "cargador" : "cliente",
    alias: (form.alias || "").trim() || undefined,
    domicilio: (form.domicilio || "").trim(),
    municipio: (form.municipio || "").trim(),
    cp: (form.cp || "").trim(),
    provincia: (form.provincia || "").trim(),
  });
  return data;
}

function dirDe(p, id = "cliente") {
  if (!p) return null;
  const d = {
    domicilio: String(p.domicilio || "").trim(),
    municipio: String(p.municipio || "").trim(),
    cp: String(p.cp || "").trim(),
    provincia: String(p.provincia || "").trim(),
  };
  if (!d.domicilio && !d.municipio && !d.cp) return null;
  return {
    ...d,
    texto: p.texto || fmtDomicilio(d),
    id,
    alias: id === "cargador" ? "Domicilio del cargador" : "Domicilio del cliente",
  };
}

export default function DestinoClienteSelect({
  clienteNif,
  clienteDir,
  sageLabel,
  valueId,
  onSelect,
  allowCreate = true,
  testid,
  hideLabel = false,
  modo = "cliente",
}) {
  const fid = React.useId();
  const esCar = modo === "cargador";
  const catalogId = esCar ? "cargador" : "cliente";
  const [items, setItems] = useState([]);
  const [parteDir, setParteDir] = useState(null);
  const [alta, setAlta] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_DOM });
  const [busy, setBusy] = useState(false);
  const nif = String(clienteNif || "").trim().toUpperCase();
  const sageTxt = String(sageLabel || "").trim();
  const clienteOpt = dirDe(clienteDir, catalogId) || parteDir;

  const reload = async () => {
    if (!nif) {
      setItems([]);
      return [];
    }
    const { data } = await api.get("/api/v1/domicilios", { params: { cliente_nif: nif, tipo: catalogId } });
    const list = data.items || [];
    setItems(list);
    setParteDir(dirDe(data.cliente, catalogId));
    return list;
  };

  useEffect(() => {
    if (!nif) {
      setItems([]);
      setParteDir(null);
      setAlta(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/v1/domicilios", { params: { cliente_nif: nif, tipo: catalogId } });
        if (cancelled) return;
        setItems(data.items || []);
        setParteDir(dirDe(data.cliente, catalogId));
      } catch {
        if (!cancelled) {
          setItems([]);
          setParteDir(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [nif, catalogId]);

  const guardarAlta = async () => {
    if (!form.domicilio.trim() && !form.municipio.trim() && !form.cp.trim()) {
      toast.error("Indica al menos calle, municipio o c" + O + "digo postal");
      return;
    }
    if (form.cp.trim() && !esCpIntl(form.cp) && !esCp(form.cp)) {
      toast.error("C" + O + "digo postal no v" + A + "lido");
      return;
    }
    setBusy(true);
    try {
      const data = await crearDomicilioCliente(nif, form, catalogId);
      await reload();
      onSelect({
        id: data.id,
        texto: data.texto || fmtDomicilio(data),
        domicilio: data.domicilio || "",
        municipio: data.municipio || "",
        cp: data.cp || "",
        provincia: data.provincia || "",
      });
      setForm({ ...EMPTY_DOM });
      setAlta(false);
      toast.success("Domicilio a" + N + "adido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const pick = (v) => {
    if (v === "sage") {
      onSelect({ id: "sage", texto: sageTxt, domicilio: "", municipio: "", cp: "", provincia: "" });
      return;
    }
    if (v === catalogId && clienteOpt) {
      onSelect({
        id: catalogId,
        texto: clienteOpt.texto || fmtDomicilio(clienteOpt),
        domicilio: clienteOpt.domicilio || "",
        municipio: clienteOpt.municipio || "",
        cp: clienteOpt.cp || "",
        provincia: clienteOpt.provincia || "",
      });
      return;
    }
    if (v === "none") {
      onSelect({ id: "libre", texto: "", domicilio: "", municipio: "", cp: "", provincia: "" });
      return;
    }
    const d = items.find((x) => x.id === v);
    if (!d) return;
    onSelect({
      id: d.id,
      texto: d.texto || fmtDomicilio(d),
      domicilio: d.domicilio || "",
      municipio: d.municipio || "",
      cp: d.cp || "",
      provincia: d.provincia || "",
    });
  };

  if (!nif) {
    return (
      <div>
        {hideLabel ? null : <Label htmlFor={fid} className="label-tag">{esCar ? "Origen (almac" + E + "n)" : "Direcci" + O + "n de destino"}</Label>}
        <div id={fid} className="mt-1 text-xs text-slate-500 border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
          {esCar ? "Elige primero el cargador." : "Elige primero el cliente."}
        </div>
      </div>
    );
  }

  const current = (
    valueId === catalogId && clienteOpt ? catalogId
    : valueId === "sage" && sageTxt ? "sage"
    : valueId && valueId !== catalogId && valueId !== "none" ? valueId
    : sageTxt ? "sage"
    : clienteOpt ? catalogId
    : "none"
  );

  return (
    <div>
      {hideLabel ? null : <Label htmlFor={fid} className="label-tag">{esCar ? "Origen (almac" + E + "n)" : "Direcci" + O + "n de destino"}</Label>}
      <Select value={current} onValueChange={pick}>
        <SelectTrigger id={fid} className="mt-1" data-testid={testid}>
          <SelectValue placeholder={"Selecciona direcci" + O + "n"} />
        </SelectTrigger>
        <SelectContent>
          {sageTxt ? (
            <SelectItem value="sage">{esCar ? "Almac" + E + "n Sage: " : "Albar" + A + "n Sage: "}{sageTxt}</SelectItem>
          ) : null}
          {clienteOpt ? (
            <SelectItem value={catalogId}>
              {esCar ? "Domicilio del cargador: " : "Domicilio del cliente: "}{clienteOpt.texto || fmtDomicilio(clienteOpt)}
            </SelectItem>
          ) : null}
          {!sageTxt && !clienteOpt ? (
            <SelectItem value="none">Sin direcci{O}n</SelectItem>
          ) : null}
          {valueId === "libre" ? (
            <SelectItem value="libre">Otra direcci{O}n (escrita)</SelectItem>
          ) : null}
          {items.map((d) => (
            <SelectItem key={d.id} value={d.id}>
              {(d.alias ? d.alias + " · " : "") + (d.texto || fmtDomicilio(d) || DASH)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="text-[11px] text-slate-500 mt-1">
        {esCar
          ? (sageTxt
            ? "Por defecto el almac" + E + "n de Sage. Puedes cambiarlo por el domicilio del cargador o por otro almac" + E + "n del DeCA."
            : "Por defecto el domicilio del cargador. Los almacenes extra se a" + N + "aden en Cargadores o aqu" + I + ".")
          : (sageTxt
            ? "Por defecto la del albar" + A + "n. Puedes cambiarla por el domicilio del cliente o por uno extra del DeCA."
            : "Por defecto el domicilio del cliente. Los extras se a" + N + "aden en Clientes o aqu" + I + ".")}
        {allowCreate ? (
          <>
            {" "}
            <button type="button" className="text-blue-700 underline" onClick={() => setAlta((v) => !v)}>
              {alta ? "Cerrar alta" : (esCar ? "A" + N + "adir almac" + E + "n" : "A" + N + "adir domicilio")}
            </button>
          </>
        ) : null}
      </div>
      {alta ? (
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          <Input
            value={form.alias}
            maxLength={LEN.alias}
            onChange={(ev) => setForm({ ...form, alias: ev.target.value })}
            placeholder={"Alias (obra, almac" + E + "n\u2026)"}
            aria-label="Alias"
          />
          <Input
            value={form.cp}
            inputMode="numeric"
            maxLength={LEN.cp}
            onChange={(ev) => setForm({ ...form, cp: ev.target.value })}
            placeholder={"C" + O + "digo postal"}
            aria-label={"C" + O + "digo postal"}
          />
          <Input
            className="sm:col-span-2"
            value={form.domicilio}
            maxLength={LEN.domicilio}
            onChange={(ev) => setForm({ ...form, domicilio: ev.target.value })}
            placeholder={"Calle y n" + U + "mero"}
            aria-label="Domicilio"
          />
          <Input
            value={form.municipio}
            maxLength={LEN.municipio}
            onChange={(ev) => setForm({ ...form, municipio: ev.target.value })}
            placeholder="Municipio"
            aria-label="Municipio"
          />
          <Input
            value={form.provincia}
            maxLength={LEN.provincia}
            onChange={(ev) => setForm({ ...form, provincia: ev.target.value })}
            placeholder="Provincia"
            aria-label="Provincia"
          />
          <div className="sm:col-span-2">
            <Button type="button" size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={busy} onClick={guardarAlta}>
              Guardar domicilio
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function DomiciliosClienteDialog({ open, onOpenChange, cliente, tipo = "cliente" }) {
  const nif = String(cliente?.nif || "").trim().toUpperCase();
  const esCar = tipo === "cargador";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_DOM });
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!nif) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/api/v1/domicilios", { params: { cliente_nif: nif, tipo: esCar ? "cargador" : "cliente" } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_DOM });
      setEdit(null);
      load();
    }
  }, [open, nif]); // eslint-disable-line

  const save = async () => {
    if (!form.domicilio.trim() && !form.municipio.trim() && !form.cp.trim()) {
      toast.error("Indica al menos calle, municipio o c" + O + "digo postal");
      return;
    }
    if (form.cp.trim() && !esCpIntl(form.cp) && !esCp(form.cp)) {
      toast.error("C" + O + "digo postal no v" + A + "lido");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        alias: form.alias.trim() || undefined,
        domicilio: form.domicilio.trim(),
        municipio: form.municipio.trim(),
        cp: form.cp.trim(),
        provincia: form.provincia.trim(),
      };
      if (edit) await api.patch(`/api/v1/domicilios/${edit.id}`, payload);
      else await crearDomicilioCliente(nif, payload, esCar ? "cargador" : "cliente");
      setForm({ ...EMPTY_DOM });
      setEdit(null);
      await load();
      toast.success(edit ? "Domicilio actualizado" : "Domicilio a" + N + "adido");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Eliminar el domicilio " + (d.alias || fmtDomicilio(d) || "") + "?")) return;
    try {
      await api.delete(`/api/v1/domicilios/${d.id}`);
      await load();
      toast.success("Domicilio eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{esCar ? "Almacenes de " : "Domicilios de "}{cliente?.razon_social || nif || (esCar ? "cargador" : "cliente")}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {esCar
              ? "El domicilio del cargador se usa por defecto como origen. Aqu" + I + " a" + N + "ades almacenes extra para elegirlos en el DeCA."
              : "El domicilio del cliente se usa por defecto en el DeCA. Aqu" + I + " solo a" + N + "ades direcciones extra."}
          </DialogDescription>
        </DialogHeader>
        {fmtDomicilio(cliente) ? (
          <div className="mt-2 border border-slate-200 bg-slate-50 rounded-md px-3 py-2">
            <div className="text-sm font-medium text-slate-900">{esCar ? "Domicilio del cargador" : "Domicilio del cliente"}</div>
            <div className="text-xs text-slate-600">{fmtDomicilio(cliente)}</div>
          </div>
        ) : null}
        <div className="space-y-2 mt-2">
          {loading ? <div className="text-xs text-slate-500">Cargando{String.fromCharCode(0x2026)}</div> : null}
          {!loading && items.length === 0 ? (
            <div className="text-sm text-slate-500 border border-slate-200 rounded-md px-3 py-4 text-center">
              {esCar ? "Este cargador no tiene almacenes extra en el DeCA." : "Este cliente no tiene domicilios en el DeCA."}
            </div>
          ) : null}
          {items.map((d) => (
            <div key={d.id} className="flex items-start justify-between gap-2 border border-slate-200 rounded-md px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">{d.alias || "Domicilio"}</div>
                <div className="text-xs text-slate-600">{d.texto || fmtDomicilio(d) || DASH}</div>
              </div>
              <div className="shrink-0 whitespace-nowrap">
                <Button size="sm" variant="outline" className="mr-1" onClick={() => { setEdit(d); setForm({ alias: d.alias || "", domicilio: d.domicilio || "", municipio: d.municipio || "", cp: d.cp || "", provincia: d.provincia || "" }); }}>
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true"/>
                </Button>
                <Button size="sm" variant="outline" className="text-red-700" onClick={() => remove(d)}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true"/>
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-200">
          <Field className="sm:col-span-2" label={edit ? "Editar domicilio" : "Nuevo domicilio"}>
            <Input className="mt-1" maxLength={LEN.alias} value={form.alias} onChange={(e) => setForm({ ...form, alias: e.target.value })} placeholder={"Alias (obra, almac" + E + "n\u2026)"}/>
          </Field>
          <Field className="sm:col-span-2" label="Domicilio">
            <Input className="mt-1" maxLength={LEN.domicilio} value={form.domicilio} onChange={(e) => setForm({ ...form, domicilio: e.target.value })}/>
          </Field>
          <Field label={"C" + O + "digo postal"}>
            <Input className="mt-1" inputMode="numeric" maxLength={LEN.cp} value={form.cp} onChange={(e) => setForm({ ...form, cp: e.target.value })}/>
          </Field>
          <Field label="Municipio">
            <Input className="mt-1" maxLength={LEN.municipio} value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })}/>
          </Field>
          <Field className="sm:col-span-2" label="Provincia">
            <Input className="mt-1" maxLength={LEN.provincia} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })}/>
          </Field>
        </div>
        <DialogFooter>
          {edit ? (
            <Button variant="outline" onClick={() => { setEdit(null); setForm({ ...EMPTY_DOM }); }}>Cancelar edici{O}n</Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
          )}
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy || !nif}>
            <Plus className="h-4 w-4 mr-1"/> {busy ? "Guardando" + String.fromCharCode(0x2026) : (edit ? "Guardar cambios" : "A" + N + "adir")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
